/**
 * SeidrIndex — shared module for the screening score (audit 2026-05-28).
 *
 * Replaces "Readiness Score" / "Score" / "Suitable / Conditional /
 * High-Risk / Not Suitable" with the SEIDR Index — same backend
 * powered_land_readiness number, but framed honestly as a screening
 * heuristic rather than a calibrated authority.
 *
 * Anything that renders a score badge should:
 *   1. Use `tierForIndex(pct)` for label + color (4 tiers below).
 *   2. Include `<SeidrIndexExplainer />` somewhere near the badge so
 *      the analyst can click through to the "what this is / isn't"
 *      modal before quoting the number externally.
 *
 * Calibration roadmap (NOT this sprint, but the modal references it):
 * track outcomes on saved projects → regress weights → publish v2 with
 * stated validation methodology. Until then it's a screening rubric
 * and we say so plainly.
 */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

// ── Tier mapping — Strong / Suitable / Conditional / Weak ────────────
// Thresholds chosen so 71 lands in Conditional (matches spec example).
// Calibration TBD — see header.
export function tierForIndex(pct) {
  if (pct == null) return { tier: 'Pending', color: '#94a3b8' };
  if (pct >= 85)   return { tier: 'Strong',      color: '#16a34a' };
  if (pct >= 75)   return { tier: 'Suitable',    color: '#22c55e' };
  if (pct >= 50)   return { tier: 'Conditional', color: '#d97706' };
  return                { tier: 'Weak',        color: '#dc2626' };
}

// ── "Screening only ⓘ" link + modal ─────────────────────────────────
export function SeidrIndexExplainer({ inline = true, style }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          color: '#94a3b8', fontSize: inline ? 10 : 11,
          textDecoration: 'underline', textDecorationStyle: 'dotted',
          textUnderlineOffset: 2, lineHeight: 1.4,
          ...style,
        }}
        title="What this score is — and isn't"
      >
        Screening only ⓘ
      </button>
      {open && createPortal(
        <SeidrIndexModal onClose={() => setOpen(false)} />,
        document.body,
      )}
    </>
  );
}

function SeidrIndexModal({ onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-label="SEIDR Index"
        style={{
          background: '#0f172a', color: '#e2e8f0',
          border: '1px solid #1f2937', borderRadius: 8,
          width: 'min(560px, 96vw)', maxHeight: '90vh', overflowY: 'auto',
          padding: '20px 22px',
          boxShadow: '0 18px 40px rgba(0,0,0,0.45)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
            SEIDR Index — what it is
          </h2>
          <button
            onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 4 }}
          >×</button>
        </div>

        <p style={{ fontSize: 13.5, lineHeight: 1.6, margin: '0 0 14px' }}>
          A weighted score across 9 measurable themes (power, network, terrain,
          environment, etc.) reflecting how well this site matches data-center
          criteria.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#86efac', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
              Use it for
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.7 }}>
              <li>Ranking sites in a search result</li>
              <li>Spotting clear outliers</li>
              <li>Triage — deciding which sites to investigate further</li>
            </ul>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#fca5a5', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
              Do not use it as
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.7 }}>
              <li>A probability of project success</li>
              <li>A financial estimate or return projection</li>
              <li>A substitute for due diligence</li>
            </ul>
          </div>
        </div>

        <p style={{ fontSize: 12, lineHeight: 1.6, color: '#94a3b8', margin: '18px 0 0', paddingTop: 14, borderTop: '1px solid #1f2937' }}>
          Weights and curves reflect SEIDR's screening rubric, not calibrated
          outcomes. Investment decisions should be based on the detailed data
          below.
        </p>
      </div>
    </div>
  );
}
